import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PageLoader from './components/PageLoader';
import { LoginModal, SignupModal } from './components/AuthModals';
import { authAPI, subscriptionAPI } from './services/api';
import Home from './pages/Home';
import './index.css';

const Pricing = lazy(() => import('./pages/Pricing'));
const MyPurchases = lazy(() => import('./pages/MyPurchases'));
const Admin = lazy(() => import('./pages/Admin'));
const Products = lazy(() => import('./pages/Products'));
const VideoDetail = lazy(() => import('./pages/VideoDetail'));
const Jarvis = lazy(() => import('./pages/Jarvis'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Support = lazy(() => import('./pages/Support'));
const SupportChat = lazy(() => import('./pages/SupportChat'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const UtilityPage = lazy(() => import('./pages/UtilityPage'));

function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Drives the --scroll-y token the parallax layers read.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let frame = 0;

    const onScroll = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--scroll-y', String(window.scrollY));
        frame = 0;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);

      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  useEffect(() => {
    const storedUser = authAPI.getCurrentUser();
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    let cancelled = false;

    subscriptionAPI
      .getStatus()
      .then((response) => {
        if (!cancelled) {
          setIsAdmin(!!response?.isAdmin);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsAdmin(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSwitchToSignup = () => {
    setShowLogin(false);
    setShowSignup(true);
  };

  const handleSwitchToLogin = () => {
    setShowSignup(false);
    setShowLogin(true);
  };

  const closeAllModals = () => {
    setShowLogin(false);
    setShowSignup(false);
  };

  const handleSocialAuthSuccess = (userData) => {
    setUser(userData);
    closeAllModals();
  };

  const handleLogout = () => {
    authAPI.logout();
    setUser(null);
  };

  return (
    <Router>
      <div className="App">
        <Navbar
          user={user}
          isAdmin={isAdmin}
          onLoginClick={() => setShowLogin(true)}
          onSignupClick={() => setShowSignup(true)}
          onLogout={handleLogout}
        />

        <main>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/my-purchases" element={<MyPurchases user={user} />} />
            <Route path="/admin" element={<Admin user={user} isAdmin={isAdmin} />} />
            <Route path="/pricing" element={<Pricing user={user} onLoginClick={() => setShowLogin(true)} />} />
            <Route path="/products" element={<Products user={user} onLoginClick={() => setShowLogin(true)} />} />
            <Route path="/products/:id" element={<VideoDetail user={user} onLoginClick={() => setShowLogin(true)} />} />
            <Route path="/jarvis" element={<Jarvis user={user} onLoginClick={() => setShowLogin(true)} />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About user={user} onLoginClick={() => setShowLogin(true)} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/chat" element={<SupportChat />} />
            <Route path="/downloads" element={<Downloads user={user} />} />
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route path="/auth/callback" element={<AuthCallback onAuthSuccess={handleSocialAuthSuccess} />} />
            <Route
              path="/privacy"
              element={(
                <UtilityPage
                  title="Privacy Policy Under Review"
                  description="NetHyTech is preparing its production privacy policy for Jarvis AI and related services."
                  eyebrow="Legal"
                  body="This placeholder route exists so the site does not ship with broken internal links. Replace it with a reviewed production privacy policy before public launch."
                  canonicalPath="/privacy"
                  noindex
                />
              )}
            />
            <Route
              path="/terms"
              element={(
                <UtilityPage
                  title="Terms of Use Under Review"
                  description="NetHyTech is preparing the production terms of use for Jarvis AI and related services."
                  eyebrow="Legal"
                  body="This placeholder route prevents the footer from sending users and crawlers to a dead page. Replace it with final terms before public launch."
                  canonicalPath="/terms"
                  noindex
                />
              )}
            />
            <Route
              path="*"
              element={(
                <UtilityPage
                  title="Page Not Found"
                  description="The page you requested could not be found on NetHyTech."
                  eyebrow="404"
                  body="The URL may be outdated or typed incorrectly. Use the main navigation to continue browsing Jarvis AI, tutorials, and support resources."
                  canonicalPath="/"
                  noindex
                />
              )}
            />
          </Routes>
          </Suspense>
        </main>

        <Footer />

        <LoginModal
          isOpen={showLogin}
          onClose={closeAllModals}
          onSwitchToSignup={handleSwitchToSignup}
        />
        <SignupModal
          isOpen={showSignup}
          onClose={closeAllModals}
          onSwitchToLogin={handleSwitchToLogin}
        />
      </div>
    </Router>
  );
}

export default App;
